<script setup lang="ts">
// A family's pages as tabs (2026-11-04, Codex Astra's navigation plan; Amine: "I lose myself in all of those options"):
// the pages that left the sidebar are tabs of the page that holds them (Jira projects: Connect a project, Repository
// readiness, Code map…), shown on each of the family's own pages, never on a detail page (a ticket, an agent), which has
// its breadcrumbs. Only the tabs this person may open; none when only one is left.
import { computed } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { FAMILIES, familyOf, familyTabs } from "@/lib/navigation";
import { useAuthStore } from "@/stores/auth";
import { useWorkspaceStore } from "@/stores/workspace";

const route = useRoute();
const auth = useAuthStore();
const workspace = useWorkspaceStore();

const shown = computed(() => {
  const at = familyOf(route.path);
  if (!at) return null;
  const tabs = familyTabs(at.family, auth.isAdmin, workspace.workspaces.some((w) => w.team_admin), workspace.current?.key);
  if (tabs.length < 2 || !tabs.some((t) => t.path === route.path)) return null;   // a detail page, or nothing to switch to
  return { title: FAMILIES[at.family].title, tabs, on: at.tab.label };
});
</script>

<template>
  <nav v-if="shown" class="family" :aria-label="shown.title" data-test="family-tabs">
    <RouterLink v-for="t in shown.tabs" :key="t.path" :to="t.path" class="tab" :aria-current="t.label === shown.on ? 'page' : undefined">
      {{ t.label }}
    </RouterLink>
  </nav>
</template>

<style scoped>
.family { display: flex; flex-wrap: wrap; gap: var(--space-1); padding: var(--space-4) var(--space-7) 0; max-width: var(--content-width);
  margin: 0 auto; width: 100%; border-bottom: 1px solid var(--line-2); }
.tab { padding: var(--space-2) var(--space-3) var(--space-3); font-size: var(--text-sm); color: var(--muted); text-decoration: none;
  border-bottom: 2px solid transparent; margin-bottom: -1px; white-space: nowrap; }
.tab:hover { color: var(--ink); }
.tab[aria-current="page"] { color: var(--ink); border-bottom-color: var(--brand); font-weight: 550; }
@media (max-width: 720px) { .family { padding: var(--space-3) var(--space-4) 0; flex-wrap: nowrap; overflow-x: auto; } }
</style>
